import type { DataGridRowSelectionState, TableFilterState, TableSortingState } from '@ibsys/kit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ConversationPipeline,
  type ExpertReportPipelineScope,
  useDeleteConversations,
  useRunConversationsPipeline,
} from '@/entities/search';
import { toastError, toastSuccess } from '@/shared/lib';
import { MASS_RUN_SEARCH_PAGE_SIZE, MASS_RUN_SELECT_ALL_LIMIT } from './constants';
import type { DialogFetchData } from './types';

/** Параметры режима массовых действий над списком диалогов */
type Params = {
  /** Массовый запуск доступен только под разрешением `ADMIN_CONVERSATION_PIPELINE` */
  canRunPipeline: boolean;
  /** Удаление доступно только под разрешением `ADMIN_CONVERSATION_DELETE` */
  canDeleteConversations: boolean;
  /**
   * Состав отмеченных строк ведёт сам раздел. Тогда отметки не сбрасываются сменой состава списка
   * и остаются тем же набором, который раздел сохраняет в выборку.
   */
  selectionOverride?: {
    rowSelection: DataGridRowSelectionState;
    onRowSelectionChange: (next: DataGridRowSelectionState) => void;
  };
  /** Ленивая загрузка среза выборки — тем же путём, что и список */
  fetchData: DialogFetchData;
  /** Действующие фильтры выборки (с наложенным слоем перехода) */
  filters: TableFilterState;
  /** Действующая сортировка выборки */
  sorting: TableSortingState;
  /** Ключ состава списка: его смена обесценивает ранее сделанный выбор строк */
  listCompositionKey: string;
  /** Диалоги удалены: показанные строки и открытая запись могут им больше не соответствовать */
  onConversationsDeleted: (conversationIds: number[]) => void;
};

/** Состояние и обработчики режима массовых действий */
type Result = {
  /** Кнопка «Действия» доступна: есть право хотя бы на одно массовое действие */
  canOpenActions: boolean;
  /** Режим массовых действий раскрыт: под разрешением и при нажатой кнопке «Действия» */
  isMassActionsActive: boolean;
  /** Единый состав отмеченных строк (поимённый выбор и «выбрать всё») */
  rowSelection: DataGridRowSelectionState;
  /** Число отмеченных диалогов */
  selectedCount: number;
  /** Отмечена хотя бы одна строка — есть над чем выполнять действие */
  hasSelection: boolean;
  /** Идёт постраничный набор id для «выбрать всё» */
  isPreparingSelection: boolean;
  /** Действие над отмеченными уже идёт или готовится их состав — новые действия заблокированы */
  isMassActionPending: boolean;
  /** Идёт запрос удаления */
  isDeletePending: boolean;
  onToggleMassActions: () => void;
  onRowSelectionChange: (next: DataGridRowSelectionState) => void;
  onRunPipeline: (pipeline: ConversationPipeline, scope?: ExpertReportPipelineScope) => void;
  /** Удаление отмеченных диалогов; завершается и при отказе сервера */
  onDeleteConversations: () => Promise<void>;
};

/**
 * Режим массовых действий над списком диалогов: раскрытие блока действий, единый состав отмеченных
 * строк (поимённый выбор и «выбрать всё» в пределах предела массовых действий) и сами действия над
 * отмеченными диалогами — запуск обработки и удаление.
 *
 * «Выбрать всё» материализует id первых `MASS_RUN_SELECT_ALL_LIMIT` диалогов текущей выборки в тот
 * же `rowSelection`, поэтому действие всегда уходит списком id как есть. Смена состава списка снимает
 * весь выбор: отмеченные id больше не соответствуют показанной выборке.
 */
export const useMassActions = ({
  canRunPipeline,
  canDeleteConversations,
  selectionOverride,
  fetchData,
  filters,
  sorting,
  listCompositionKey,
  onConversationsDeleted,
}: Params): Result => {
  const { t } = useTranslation(['dialogs']);
  const { mutate: runPipeline, isPending: isRunPipelinePending } = useRunConversationsPipeline();
  const { mutateAsync: deleteConversations, isPending: isDeletePending } = useDeleteConversations();

  // Режим массовых действий раскрывается кнопкой «Действия»: только в нём видны столбец выбора
  // в строках и блок действий под строкой поиска.
  const [isMassActionsOpen, setIsMassActionsOpen] = useState(false);

  // Массовый выбор диалогов: поимённый выбор строк и «выбрать всё» хранятся единым составом
  // отмеченных id — «выбрать всё» материализует id первых диалогов выборки в тот же rowSelection.
  const [ownRowSelection, setOwnRowSelection] = useState<DataGridRowSelectionState>({});
  const rowSelection = selectionOverride?.rowSelection ?? ownRowSelection;
  const setRowSelection = selectionOverride?.onRowSelectionChange ?? setOwnRowSelection;
  // Идёт набор id для «выбрать всё» — блок действий занят до его завершения.
  const [isPreparingSelection, setIsPreparingSelection] = useState(false);
  // Актуальный состав выбора для обработчика мастер-чекбокса: сравнение до/после без устаревшего замыкания.
  const rowSelectionRef = useRef(rowSelection);
  rowSelectionRef.current = rowSelection;
  // Свой состав отметок обесценивается сменой состава списка, чужой — нет: раздел ведёт его сам.
  const isOwnSelection = selectionOverride == null;

  // Отмеченные диалоги — единый состав для поимённого выбора и «выбрать всё».
  const selectedConversationIds = useMemo(
    () =>
      Object.entries(rowSelection)
        .filter(([, selected]) => selected)
        .map(([rowId]) => Number(rowId)),
    [rowSelection],
  );

  const canOpenActions = canRunPipeline || canDeleteConversations;
  const isMassActionsActive = canOpenActions && isMassActionsOpen;
  const hasSelection = selectedConversationIds.length > 0;
  // Действия над одним составом не пересекаются: пока одно идёт, другое не запускается.
  const isMassActionPending = isRunPipelinePending || isDeletePending || isPreparingSelection;

  // Смена состава списка (фильтры/поиск/сортировка) обесценивает ранее сделанный выбор строк
  // и режим «выбрать все»: выбранные id больше не соответствуют показанной выборке. Состав,
  // который ведёт сам раздел, это не касается — там отметки набирают разными отборами.
  useEffect(() => {
    if (isOwnSelection) setOwnRowSelection({});
  }, [listCompositionKey, isOwnSelection]);

  // Кнопка «Действия»: раскрывает/сворачивает режим; сворачивание снимает свой массовый выбор.
  const handleToggleMassActions = useCallback(() => {
    setIsMassActionsOpen((open) => {
      if (open && isOwnSelection) setOwnRowSelection({});
      return !open;
    });
  }, [isOwnSelection]);

  // «Выбрать всё»: набираем id первых MASS_RUN_SELECT_ALL_LIMIT диалогов текущей выборки (тем же
  // срезом, что и список: действующие поиск/фильтры/сортировка) и отмечаем их как выбранные. Поиск
  // отдаёт не более страницы за запрос, поэтому предел набирается постранично.
  const selectAllUpToLimit = useCallback(async () => {
    setIsPreparingSelection(true);
    try {
      const next: DataGridRowSelectionState = {};
      let count = 0;
      let total = 0;
      let offset = 0;
      while (count < MASS_RUN_SELECT_ALL_LIMIT) {
        const pageLimit = Math.min(MASS_RUN_SEARCH_PAGE_SIZE, MASS_RUN_SELECT_ALL_LIMIT - count);
        const page = await fetchData({ offset, limit: pageLimit, filters, sorting });
        total = page.total;
        for (const item of page.items) {
          next[String(item.id)] = true;
          count += 1;
        }
        offset += page.items.length;
        // Выборка исчерпана — следующих страниц нет
        if (offset >= total || page.items.length === 0) break;
      }
      setRowSelection(next);
    } catch {
      toastError(t('dialogs:dialogListSection.massActions.selectAllError'));
    } finally {
      setIsPreparingSelection(false);
    }
  }, [fetchData, filters, sorting, setRowSelection, t]);

  // Изменение мульти-выбора DataGrid. Мастер-чекбокс кита выделяет разом все строки во вьюпорте
  // (>1 строки за жест) — такой массовый жест трактуем как «выбрать всю текущую выборку в пределах
  // предела», массовое снятие — как очистку. Изменение ровно одной строки — поимённый выбор.
  const handleRowSelectionChange = useCallback(
    (next: DataGridRowSelectionState) => {
      if (isPreparingSelection) return;
      const prev = rowSelectionRef.current;
      const addedCount = Object.keys(next).filter((id) => next[id] && !prev[id]).length;
      const removedCount = Object.keys(prev).filter((id) => prev[id] && !next[id]).length;
      if (addedCount > 1) {
        // Мастер-чекбокс «выбрать всё»: сразу собираем всю выборку до предела и выставляем итоговый
        // состав разом, не отмечая промежуточно только видимый вьюпорт. Иначе счётчик мигает числом
        // видимых строк (например 25), а через пару секунд — итоговым (500/N). На время сбора блок
        // действий показывает индикатор ожидания.
        void selectAllUpToLimit();
        return;
      }
      if (removedCount > 1) {
        setRowSelection({});
        return;
      }
      setRowSelection(next);
    },
    [isPreparingSelection, selectAllUpToLimit, setRowSelection],
  );

  // Массовый запуск обработки по отмеченным диалогам. Выборка уже материализована составом id
  // (поимённо либо «выбрать всё» в пределах предела), поэтому уходит списком id как есть.
  // Область запуска задаётся только у генерации заключений; без неё запуск прежний — целиком.
  const handleRunPipeline = useCallback(
    (pipeline: ConversationPipeline, scope?: ExpertReportPipelineScope) => {
      if (!hasSelection || isMassActionPending) return;
      runPipeline(
        { filters: { conversationIds: selectedConversationIds }, pipeline, ...scope },
        {
          onSuccess: (result) => {
            // При усечении выборки по пределу подтверждение это отмечает.
            toastSuccess(
              result.truncated
                ? t('dialogs:dialogListSection.runPipeline.successTruncated', {
                    count: result.accepted,
                    limit: MASS_RUN_SELECT_ALL_LIMIT,
                  })
                : t('dialogs:dialogListSection.runPipeline.success', { count: result.accepted }),
            );
            // Свой выбор после запуска снимается; состав, который ведёт раздел, запуск не трогает —
            // это набор записей выборки, а не разовая отметка под действие.
            if (isOwnSelection) setOwnRowSelection({});
          },
        },
      );
    },
    [hasSelection, isMassActionPending, selectedConversationIds, runPipeline, isOwnSelection, t],
  );

  // Удаление отмеченных диалогов тем же составом id, что и запуск обработки. Промис не отклоняется:
  // подтверждение закрывается и при отказе, причину которого уже показало общее уведомление об
  // ошибке запроса, — отметки тогда остаются, чтобы удаление можно было повторить.
  const handleDeleteConversations = useCallback(async () => {
    if (!hasSelection || isMassActionPending) return;
    const result = await deleteConversations({ filters: { conversationIds: selectedConversationIds } }).catch(
      () => null,
    );
    if (!result) return;
    toastSuccess(t('dialogs:dialogListSection.deleteDialogs.success', { count: result.deleted }));
    // Удалённые диалоги отмечать больше нечем: свой выбор снимается, как и после запуска обработки.
    if (isOwnSelection) setOwnRowSelection({});
    onConversationsDeleted(result.conversationIds);
  }, [
    hasSelection,
    isMassActionPending,
    selectedConversationIds,
    deleteConversations,
    isOwnSelection,
    onConversationsDeleted,
    t,
  ]);

  return {
    canOpenActions,
    isMassActionsActive,
    rowSelection,
    selectedCount: selectedConversationIds.length,
    hasSelection,
    isPreparingSelection,
    isMassActionPending,
    isDeletePending,
    onToggleMassActions: handleToggleMassActions,
    onRowSelectionChange: handleRowSelectionChange,
    onRunPipeline: handleRunPipeline,
    onDeleteConversations: handleDeleteConversations,
  };
};
